import { ChevronUp, Minus, X } from 'lucide-react'
import { useState } from 'react'

import { cn } from '@/lib/utils'
import { useAgentName } from './agentName'
import { AgentSurface, PANEL_WIDTH } from './AgentSurface'
import { Person } from './contacts'
import { ContactStrip } from './ContactStrip'

/**
 * The agent as a phone-sized panel docked bottom right, opened from the avatar
 * in the top bar or the tab in the bottom one.
 */
export function AgentChatModal({ onClose, notified }: { onClose: () => void; notified?: boolean }) {
  // Minimising keeps the conversation up but out of the way, which is a different
  // intent from dismissing it — the header stays docked and everything else folds.
  const [minimized, setMinimized] = useState(false)
  const agentName = useAgentName()
  const [person, setPerson] = useState<Person | null>(null)

  return (
    <div
      style={{ width: PANEL_WIDTH }}
      className={cn(
        'border-ink-100 absolute right-3 bottom-3 flex max-w-[calc(100%-1.5rem)] flex-col overflow-hidden rounded-xl border bg-white shadow-xl',
        !minimized && 'h-[calc(100%-1.5rem)] max-h-[560px]'
      )}
    >
      <div className="border-ink-100 flex items-center gap-1 border-b py-2.5 pr-2 pl-3">
        <span className="text-ink mr-auto text-sm font-semibold">Ask {agentName}</span>
        <button
          type="button"
          onClick={() => setMinimized((was) => !was)}
          aria-label={minimized ? `Expand Ask ${agentName}` : `Minimize Ask ${agentName}`}
          aria-expanded={!minimized}
          className="hover:bg-ink-50 rounded p-1"
        >
          {minimized ? (
            <ChevronUp className="text-ink-300 size-4" />
          ) : (
            <Minus className="text-ink-300 size-4" />
          )}
        </button>
        <button
          type="button"
          onClick={onClose}
          aria-label={`Close Ask ${agentName}`}
          className="hover:bg-ink-50 rounded p-1"
        >
          <X className="text-ink-300 size-4" />
        </button>
      </div>

      {!minimized && (
        <>
          <ContactStrip person={person} onSelect={setPerson} unread={notified} />
          <AgentSurface person={person} notified={notified} />
        </>
      )}
    </div>
  )
}
